export const AdvisoryCallStatus = {
  PENDING: 'PENDING',
  STARTED_BRIEF: 'STARTED_BRIEF',
  COMPLETED_BRIEF: 'COMPLETED_BRIEF',
  ERROR: 'ERROR',
  PREPARE_EXPORTING: 'PREPARE_EXPORTING',
  EXPORTING: 'EXPORTING',
  COMPLETED_EXPORT: 'COMPLETED_EXPORT',
  CHAT_IN_PROGRESS: 'CHAT_IN_PROGRESS',
  CHAT_ERROR: 'CHAT_ERROR',
} as const

export type AdvisoryCallStatusType = (typeof AdvisoryCallStatus)[keyof typeof AdvisoryCallStatus]

export const AdvisoryFindingType = {
  METRICS: 'metrics',
  FINDINGS: 'findings',
  RECOMMENDATIONS: 'recommendations',
} as const

export type AdvisoryFindingTypeValue = (typeof AdvisoryFindingType)[keyof typeof AdvisoryFindingType]

export type AdvisoryStatus = 'Active' | 'Pending' | 'Failed'

export type DashboardCard = {
  id: string
  title: string
  value: string
  trend: string
}

export type MappingRow = {
  id: string
  name: string
  platform: string
  status: AdvisoryStatus
  updatedAt: string
}

export type SurveyFormRow = {
  id: string
  name: string
  responses: number
  status: AdvisoryStatus
  updatedAt: string
}

export type AdvisoryAiPlatformType =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9

/**
 * 'unknown' means the grid response carried no profile field at all — the API
 * change that adds it has not shipped yet. Callers must not gate on 'unknown',
 * or every company looks unfilled and creation is blocked everywhere.
 */
export type IndustryProfileStatus = 'filled' | 'empty' | 'unknown'

export type AdvisoryAiOrgRow = {
  id: string
  companyId: string
  name: string
  companyName: string
  typeId: number
  platformTypeLabel: string
  refreshStatus: string
  lastSuccessfulRefresh: string | null
  industryProfileStatus: IndustryProfileStatus
}

export type AdvisoryAiOrgFilters = {
  clientId: string
  name?: string
  companyName?: string
  typeId?: number
}

export type AdvisoryAiReportType =
  | 'profitability'
  | 'accounts_receivable'
  | 'accounts_payable'
  | 'balance_sheet'
  | 'cashflow'

export type GenerateAdvisoryAiReportInput = {
  orgId: string
  startDate: Date | string
  endDate: Date | string
  reportTypes: AdvisoryAiReportType[]
  clientId: string
}

export type GenerateAdvisoryAiReportResponse = {
  advisoryCallId: string
}

export type AdvisoryAiReportStatus = AdvisoryCallStatusType | string

export type AdvisoryAiExportStatus = 'PENDING_EXPORT' | 'EXPORTING' | 'COMPLETED_EXPORT' | 'ERROR' | string

export type AdvisoryAiExportFormat = 'pdf' | 'pdf-summary' | 'ppt' | 'email' | 'video'

export type AdvisoryAiExportEnqueueStatus =
  | 'QUEUED'
  | 'REQUEUED'
  | 'ALREADY_PENDING'
  | 'ALREADY_COMPLETED'
  | string

export type AdvisoryAiOrgProfile = {
  companyId: string
  name: string
  companyName: string
  profile: string
  internalMemory: string
}

export type UpdateAdvisoryAiOrgProfileInput = {
  clientId: string
  companyId: string
  profile: string
  internalMemory?: string
}

export type AdvisoryAiOrgInternalMemory = {
  orgId: string
  internalMemory: string
}

export type UpdateAdvisoryAiOrgInternalMemoryInput = {
  clientId: string
  orgId: string
  internalMemory: string
}

export type AdvisoryAiReportRawData = Record<string, Array<Record<string, unknown>>>

/** How extracted accounts are grouped in the analysis. Drafts start at null (backend default). */
export type AdvisoryAccountMapping = 'Reporting Code' | 'Account Type'

export type AdvisoryAiReportDetail = {
  advisoryCallId: string
  reports: string[]
  startDate: string | null
  endDate: string | null
  meetingDate: string | null
  meetingTitle?: string | null
  createdAt: string | null
  status: AdvisoryAiReportStatus
  exportType: string | null
  summary: string | null
  notes: string | null
  internalMemory: string | null
  accountMapping?: AdvisoryAccountMapping | null
  includeHeadlinerSummary?: boolean
  headlinerSummary?: string | null
  revaluationMissing?: boolean
  agendaItems?: AdvisoryAgendaItemSummary[]
  findings?: AdvisoryFinding[]
  reportJson?: AdvisoryAiReportRawData | null
}

export type AdvisoryAiExportEnqueueResponse = {
  reportId: string
  exportFormat: AdvisoryAiExportFormat
  status: AdvisoryAiExportEnqueueStatus
}

export type AdvisoryAiOrgReportRow = {
  advisoryCallId: string
  reports: string[]
  startDate: string | null
  endDate: string | null
  meetingDate: string | null
  createdAt: string | null
  status: AdvisoryAiReportStatus
  exportType: string | null
  accountMapping?: AdvisoryAccountMapping | null
  includeHeadlinerSummary?: boolean
  headlinerSummary?: string | null
  revaluationMissing?: boolean
  /**
   * Normalized UI hint for whether this user may open, run, export or delete the meeting.
   * A missing backend field is normalized to true for deploy compatibility; the API remains the
   * authorization boundary.
   */
  canInteract: boolean
}

export type AdvisoryAiOrgReportsResponse = {
  rows: AdvisoryAiOrgReportRow[]
  totalCount: number
}

export type AdvisoryAgendaItem = {
  advisoryAgendaId: string
  orgId: string
  title: string
  description: string
  createdAt: Date
  updatedAt: Date
  createdBy?: string
  updatedBy?: string
}

export type UpdateAdvisoryCallAgendaItemInput = {
  advisoryAgendaId?: string | null
  title: string
  description: string
}

export type CreateAdvisoryReportInput = {
  orgId: string
  clientId: string
}

export type CreateAdvisoryReportResponse = {
  advisoryCallId: string
  orgId: string
  status: string
  createdAt: string
}

export type AdvisoryFinding = {
  advisoryFindingId: string
  advisoryCallId: string
  advisoryAgendaId: string
  type: AdvisoryFindingTypeValue | string
  heading?: string | null
  description: string
  createdAt: string
  reports?: string[]
  /** When true, the item is excluded from generated outputs. */
  hidden?: boolean
}

export type AdvisoryFindingCreate = {
  type: AdvisoryFindingTypeValue | string
  heading?: string | null
  description: string
  hidden?: boolean
}

export type AdvisoryFindingUpdate = {
  advisoryFindingId: string
  type: AdvisoryFindingTypeValue | string
  heading?: string | null
  description: string
  hidden?: boolean
}

export type BatchUpdateFindingsInput = {
  reportId: string
  clientId: string
  advisoryAgendaId: string
  created: AdvisoryFindingCreate[]
  updated: AdvisoryFindingUpdate[]
  deleted: string[]
}

export type AdvisoryExport = {
  advisoryExportId: string
  advisoryCallId: string
  exportType: AdvisoryAiExportFormat
  status: AdvisoryAiExportStatus
  url: string | null
  fileSize?: number | null
  pageCount?: number | null
  createdAt: string
  updatedAt: string
  contentUpdatedAt?: string | null
  isOutdated?: boolean
}

export type AdvisoryAgendaItemSummary = {
  advisoryAgendaId: string
  title: string
  description: string
  createdAt: string
}

export type AnalyzeAdvisoryReportInput = {
  reportId: string
  clientId: string
  reportTypes: AdvisoryAiReportType[]
}

export type UpdateAdvisoryCallInput = {
  reportId: string
  clientId: string
  summary?: string | null
  notes?: string | null
  internalMemory?: string | null
  meetingTitle?: string | null
  status?: string | null
  startDate?: string | null
  endDate?: string | null
  meetingDate?: string | null
  reports?: string[] | null
  // Both optional — omitted (undefined) leaves the stored value unchanged.
  // Invalid accountMapping values are rejected by the backend with a 400.
  accountMapping?: AdvisoryAccountMapping
  includeHeadlinerSummary?: boolean
  agendaItems?: UpdateAdvisoryCallAgendaItemInput[] | null
}

export type AdvisoryAgendaTemplateLine = {
  title: string
  description: string
}

export type AdvisoryAgendaTemplate = {
  advisoryAgendaTemplateId: string
  clientId: string
  title: string
  items: AdvisoryAgendaTemplateLine[]
  createdAt: Date
  updatedAt: Date
  createdBy?: string
  updatedBy?: string
}

export type CreateAdvisoryAgendaTemplateInput = {
  clientId: string
  title: string
  items?: AdvisoryAgendaTemplateLine[]
}

export type UpdateAdvisoryAgendaTemplateInput = {
  templateId: string
  clientId: string
  title: string
  items?: AdvisoryAgendaTemplateLine[]
}

export type AdvisoryAiThemeColors = {
  clientId: string
  primaryColor: string
  secondaryColor: string
  textColor: string
}

export type UpdateAdvisoryAiThemeColorsInput = {
  clientId: string
  primaryColor?: string
  secondaryColor?: string
  textColor?: string
}

export type DeepDiveChatMessage = {
  role: 'user' | 'assistant'
  content: string
}

export type DeepDiveChatRequest = {
  advisoryCallId: string
  clientId: string
  question: string
  messages: DeepDiveChatMessage[]
}

export type DeepDiveChatResponseMessage = {
  messageId: string
  role: 'assistant'
  content: string
  intent: 'clarification' | 'update' | 'summarize' | 'general'
  updatedEntities: string[]
  insertDate: string
  insertUserId: string
}

export type DeepDiveChatQueuedResponse = {
  status: 'queued'
}

export type DeepDiveChatMessageResponse = {
  message: DeepDiveChatResponseMessage
}

export type DeepDiveChatResponse = DeepDiveChatMessageResponse | DeepDiveChatQueuedResponse

export function isQueuedDeepDiveResponse(
  response: DeepDiveChatResponse
): response is DeepDiveChatQueuedResponse {
  return 'status' in response && response.status === 'queued'
}
